import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from "../Common/AppOverlay";
import {
  downloadInspectionDocument,
  getQueryResponseReview,
  updateQueryResponseReview,
  uploadInspectionChecklistQueryAttachment,
} from "../../helpers/backend_helper";
import "./QueryResponseReview.scss";

const stateLabels = {
  Draft: "Draft response",
  AwaitingReview: "Awaiting review",
  ChangesRequested: "Changes requested",
  Posted: "Response posted",
  Cancelled: "Review cancelled",
};
const editableBody = (data) => data?.canDraft && data.draft?.state === "Posted" ? "" : data?.draft?.body || "";

export default function QueryResponseReview({
  readOnly = false,
  postedInConversation = false,
  inspectionId,
  queryId,
  onChanged,
  refreshSnapshot,
  children,
}) {
  const [responseData, setData] = useState(null);
  const data = readOnly && responseData ? { ...responseData, canDraft: false, canApprove: false, canComment: false } : responseData;
  const [body, setBody] = useState("");
  const [feedback, setFeedback] = useState("");
  const [reviewers, setReviewers] = useState([]);
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [closingDialog, setClosingDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const liveDraft = useRef();
  liveDraft.current = { data, body, reviewers };
  useEffect(() => {
    if (!refreshSnapshot) return;
    setError("");
    const current = liveDraft.current;
    const dirty = current.data && current.body !== editableBody(current.data);
    const reviewerDirty = current.data && JSON.stringify(current.reviewers) !== JSON.stringify(current.data.reviewerIds || []);
    // Keep the old edit version when remote edits conflict. The API's version
    // check will prevent an unsaved local response from overwriting them.
    const conflict = dirty && current.data.draft?.version !== refreshSnapshot.draft?.version;
    setData(conflict ? { ...refreshSnapshot, draft: { ...refreshSnapshot.draft, version: current.data.draft?.version || 0 } } : refreshSnapshot);
    if (!dirty) setBody(editableBody(refreshSnapshot));
    if (!reviewerDirty) setReviewers(refreshSnapshot.reviewerIds || []);
    if (dirty) setNotice(conflict ? "This response changed on the server. Your unsaved text is retained; reconcile it before saving." : "Refreshed. Your unsaved text is retained.");
  }, [refreshSnapshot]);
  const displayedDialog = dialog || closingDialog;
  const closeDialog = () => {
    setClosingDialog(dialog);
    setDialog(null);
  };

  const apply = (next) => {
    setData(next);
    setBody(editableBody(next));
    setReviewers(next.reviewerIds || []);
  };
  useEffect(() => {
    let active = true;
    setData(null);
    setError("");
    getQueryResponseReview(inspectionId, queryId)
      .then((result) => {
        if (!active) return;
        if (!result?.status)
          throw new Error(result?.message || "Unable to load response review.");
        apply(result.data);
      })
      .catch((reason) => {
        if (active) setError(reason.message);
      });
    return () => {
      active = false;
    };
  }, [inspectionId, queryId]);

  const act = async (action) => {
    if (readOnly) return;
    if (busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      // Save first when revising: this withdraws the old version from approval.
      // Attachments may only change while the response is in Draft state.
      let version = data.draft?.version || 0;
      if ((action === "save" || action === "send") && files.length) {
        const saved = await updateQueryResponseReview(
          inspectionId,
          queryId,
          "save",
          { version, body }
        );
        if (!saved?.status)
          throw new Error(saved?.message || "Unable to save draft.");
        version = saved.data.version;
        setData((current) => ({
          ...current,
          draft: { ...current.draft, body, version, state: "Draft" },
          canApprove: false,
          canComment: false,
        }));
        for (const file of files) {
          const form = new FormData();
          form.append("attachmentType", "Response");
          form.append("document", file);
          const uploaded = await uploadInspectionChecklistQueryAttachment(
            inspectionId,
            queryId,
            form
          );
          if (!uploaded?.status)
            throw new Error(
              uploaded?.message || `Unable to upload ${file.name}.`
            );
          setFiles((current) => current.filter((item) => item !== file));
        }
      }
      const result = await updateQueryResponseReview(
        inspectionId,
        queryId,
        action,
        {
          version,
          body:
            action === "comment" || action === "request-changes"
              ? feedback
              : body,
          reviewerIds: reviewers.filter((id) =>
            (data.partners || (data.partner ? [data.partner] : [])).some((partner) => partner.id === id)
          ),
          message,
        }
      );
      if (!result?.status)
        throw new Error(result?.message || "Unable to update response review.");
      closeDialog();
      setFeedback("");
      setMessage("");
      const refreshed = await getQueryResponseReview(inspectionId, queryId);
      if (!refreshed?.status)
        throw new Error(
          refreshed?.message ||
            "Saved. Reopen the query to refresh its review state."
        );
      apply(refreshed.data);
      setNotice(
        action === "approve"
          ? "Response approved and posted to the inspection team."
          : action === "send"
          ? "Reviewers and the Engagement Partner have been assigned review tasks."
          : action === "comment"
          ? "Feedback added."
          : action === "request-changes"
          ? "Changes requested from the assignee."
          : "Draft saved."
      );
      if (["approve", "send", "request-changes"].includes(action)) await onChanged?.();
    } catch (reason) {
      setError(
        reason.message ||
          "Unable to update response review. Reopen the query to refresh the latest version."
      );
    } finally {
      setBusy(false);
    }
  };
  const download = async (file) => {
    try {
      await downloadInspectionDocument(inspectionId, file.id, file.fileName);
    } catch (reason) {
      setError(reason.message || "Unable to download attachment.");
    }
  };
  if (!data)
    return (
      <section className="query-response-review" aria-busy={!error}>
        {error ? <p role="alert">{error}</p> : "Loading response review…"}
      </section>
    );
  if (!data.canView)
    return (
      children || (
        <p className="query-response-review__note">
          Draft responses require Engagement Partner approval before they are
          posted here.
        </p>
      )
    );
  const state = data.draft?.state || "Draft";
  const dirty = body !== (data.draft?.body || "") || files.length > 0;
  const partners = data.partners || (data.partner ? [data.partner] : []);
  const team = partners.map((member) => ({
    value: member.id,
    label: member.name,
  }));
  return (
    <section className="query-response-review" aria-busy={busy}>
      <header>
        <h3>Query response</h3>
        <span className={`query-response-review__status is-${state}`}>
          {data.queryResolved ? "Resolved" : state === "Posted" && data.canDraft ? "Reply required" : stateLabels[state]}
        </span>
      </header>
      {error && (
        <p className="query-response-review__error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="query-response-review__notice" role="status">
          {notice}
        </p>
      )}
      {(data.events || []).filter(event => ["request-changes", "comment"].includes(event.eventType)).map(event => (
        <article className="query-response-review__card" key={event.id}>
          <b>{event.eventType === "request-changes" ? "Changes requested" : "Review feedback"}</b>
          <p>{event.text}</p>
          <small>{event.authorName}</small>
        </article>
      ))}
      <div className="query-response-review__layout">
        <div>
          {state === "Posted" && postedInConversation && !data.canDraft && <p className="query-response-review__notice">The approved response is shown in the conversation above.</p>}
          {!(state === "Posted" && postedInConversation && !data.canDraft) && (
            <div className="query-response-review__card">
              <label htmlFor={`draft-response-${queryId}`}>
                <b>
                  {state === "Posted" && data.canDraft ? "Reply to inspection follow-up" : state === "Posted" ? "Posted response" : "Draft response"}
                </b>
              </label>
              {data.canDraft ? (
                <>
                  <textarea
                    id={`draft-response-${queryId}`}
                    rows={7}
                    maxLength={4000}
                    value={body}
                    disabled={busy}
                    onChange={(event) => setBody(event.target.value)}
                    placeholder="Prepare your response and supporting explanation…"
                  />
                  <small>{body.length}/4000</small>
                </>
              ) : (
                <p className="query-response-review__body">
                  {data.draft?.body ||
                    "The assignee has not saved a draft response yet."}
                </p>
              )}
              {(data.attachments || []).map((file) => (
                <button
                  className="query-response-review__file"
                  type="button"
                  key={file.id}
                  onClick={() => download(file)}
                >
                  <i className="bx bx-file" /> {file.fileName}
                  <i className="bx bx-download" />
                </button>
              ))}
              {files.map((file, index) => (
                <div
                  className="query-response-review__file"
                  key={`${file.name}-${index}`}
                >
                  {file.name}
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      setFiles((current) =>
                        current.filter((item) => item !== file)
                      )
                    }
                  >
                    Remove
                  </button>
                </div>
              ))}
              {data.canDraft && (
                <label className="query-response-review__upload">
                  <i className="bx bx-paperclip" /> Add attachments
                  <input
                    type="file"
                    hidden
                    multiple
                    disabled={busy}
                    onChange={(event) => {
                      setFiles((current) => [
                        ...current,
                        ...Array.from(event.target.files || []),
                      ]);
                      event.target.value = "";
                    }}
                  />
                </label>
              )}
              <p className="query-response-review__note">
                {state === "Posted" && data.canDraft
                  ? "The inspection team requested a further response. Draft your reply and send it for Engagement Partner approval. The previous approved response remains in the conversation."
                  : state === "Posted"
                  ? "The Engagement Partner approved and posted this response."
                  : "Reviewers provide advisory feedback. Any active firm Engagement Partner can approve and post."}
              </p>
              {data.canDraft && (
                <footer>
                  <button
                    type="button"
                    disabled={busy || !body.trim()}
                    onClick={() => act("save")}
                  >
                    Save draft
                  </button>
                  <button
                    type="button"
                    className="is-primary"
                    disabled={busy || !body.trim()}
                    onClick={() => setDialog("send")}
                  >
                    {state === "AwaitingReview"
                      ? "Send updated draft for review"
                      : "Send for review"}
                  </button>
                </footer>
              )}
              {data.canApprove && (
                <>
                  <p className="query-response-review__note">
                    Your approval will post this response immediately.
                  </p>
                  <footer>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setFeedback("");
                        setDialog("request-changes");
                      }}
                    >
                      Request changes
                    </button>
                    <button
                      type="button"
                      className="is-primary"
                      disabled={busy || dirty}
                      onClick={() => setDialog("approve")}
                    >
                      Approve
                    </button>
                  </footer>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <Modal
        isOpen={!readOnly && Boolean(dialog)}
        toggle={() => !busy && closeDialog()}
        onClosed={() => setClosingDialog(null)}
        centered
        className="query-response-review-dialog"
      >
        <ModalHeader toggle={() => !busy && closeDialog()}>
          {displayedDialog === "send"
            ? "Send response for review"
            : displayedDialog === "approve"
            ? "Approve response?"
            : "Request changes"}
        </ModalHeader>
        <ModalBody>
          {displayedDialog === "send" ? (
            <>
              <label htmlFor="response-reviewers">Reviewers *</label>
              <Select
                classNamePrefix="query-response-review-select"
                inputId="response-reviewers"
                isMulti
                options={team}
                value={team.filter((option) =>
                  reviewers.includes(option.value)
                )}
                onChange={(values) =>
                  setReviewers((values || []).map((option) => option.value))
                }
                isDisabled={busy}
                placeholder="Select engagement partners"
              />
              <p className="query-response-review__note">
                Reviewers provide feedback. Any active firm Engagement Partner can approve and
                post.
              </p>
              <h6>Final approval</h6>
              <p>{partners.length ? `Any active Engagement Partner in this firm may approve and post: ${partners.map((partner) => partner.name).join(", ")}.` : "No active Engagement Partners are available in this firm."}</p>
              <label htmlFor="response-review-message">
                Message to reviewers (optional)
              </label>
              <textarea
                id="response-review-message"
                rows={3}
                maxLength={1000}
                value={message}
                disabled={busy}
                onChange={(event) => setMessage(event.target.value)}
              />
            </>
          ) : displayedDialog === "approve" ? (
            <p>
              The response will be posted immediately and become visible to the
              inspection team.
            </p>
          ) : (
            <>
              <label htmlFor="response-changes">Changes needed *</label>
              <textarea
                id="response-changes"
                rows={4}
                maxLength={4000}
                value={feedback}
                disabled={busy}
                onChange={(event) => setFeedback(event.target.value)}
              />
            </>
          )}
          {error && (
            <p role="alert" className="query-response-review__error">
              {error}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <button type="button" disabled={busy} onClick={closeDialog}>
            Cancel
          </button>
          <button
            type="button"
            className="is-primary"
            disabled={
              busy ||
              (dialog === "send" && !team.some((option) => reviewers.includes(option.value))) ||
              (dialog === "request-changes" && !feedback.trim())
            }
            onClick={() => act(dialog)}
          >
            {busy
              ? "Saving…"
              : displayedDialog === "send"
              ? "Send for review"
              : displayedDialog === "approve"
              ? "Approve"
              : "Request changes"}
          </button>
        </ModalFooter>
      </Modal>
    </section>
  );
}
